<template>
  <div id="cesiumViewer" class="fullSize"></div>
</template>

<script>
import Vue from 'vue'
import { mapGetters } from 'vuex'
import CesiumPlus from '@/libs/cesiumExtents/CesiumPlus'
import * as markerStyles from '@/utils/markerStyles'
import * as turf from '@turf/turf'
import { treeForeach, searchBFS } from '@/utils/tree'
import _ from 'lodash'
import { dataset2table, coordTransform, validateCameraInfo } from '@/utils/util'
import { MeasureTools } from '@/utils/measureTools'
import { IframeMsgConnecter } from '@/libs/iframeMessage/IframeMsgConnecter'
import SppucManager, { isTw } from '@/views/operatingPlatform/Marker/components/sppuc/SppucManager'
// 是否开启融合通信模块
const isSppuc = process.env.VUE_APP_SPPUC === 'true'
// 显示标注名称最大距离
const MAX_DISPLAY_DISTANCE = 1000

export default {
  name: 'CesiumViewer',
  // props: {
  //   // 自定义 Marker 点击事件
  //   customClickMarker: {
  //     type: Function
  //   }
  // },
  inject: ['shared', 'channel'],
  computed: {
    ...mapGetters(['isDrawState',
      'isRightDrawVisible',
      'isShowTop',
      'isInMarkerEditing',
      'selectedMarker',
      'curMapId',
      'layerList',
      'markerDatas',
      'apiSourceDatas',
      'modelHeight',
      'defaultViewPoint',
      'frequentViewPoint',
      'layerVisibleStorage',
      'layerTree',
      'isTemplate',
      'uiHiddenState',
      'sppucAccount'
    ]),
    layerVisible () {
      if (!this.layerVisibleStorage || !this.layerVisibleStorage[this.curMapId]) {
        return {}
      }
      return this.layerVisibleStorage[this.curMapId]
    },
    sppucLayers () {
      if (!this.layerList) {
        return []
      }
      return this.layerList.filter(item => { return item.module === 'sppuc' })
    }
  },
  watch: {
    isRightDrawVisible (val) {
      if (this.isTemplate || this.shared) return
      if (val === false) {
        document.getElementById('navigationContainer').style.right = '10px'
      } else {
        document.getElementById('navigationContainer').style.right = '270px'
      }
    },
    // isShowTop (val) {
    //   if (val === false) {
    //     document.getElementById('navigationContainer').style.top = '10px'
    //   } else {
    //     document.getElementById('navigationContainer').style.top = '50px'
    //   }
    // },
    layerList (val) {
      this.isUpdateVisible = true
      // if (val && this.markerDatas) {
      //   this.loadMarkers()
      // }

      // 更新api标注样式
      if (!val) {
        return
      }
      for (let i = 0; i < val.length; i++) {
        const layer = val[i]
        if (layer.dataSourceType === 'API') {
          const config = JSON.parse(layer.config)
          if (this.apiSourceDatas[config.dataSourceId]) {
            const markerStyle = markerStyles.adapterPointStyle(layer.pointStyle)
            // 散点图
            if (markerStyle.type === 'billboard') {
              const heatMap = this.$cesiumPlus.heatMapManager.getHeatMapById(layer.id)
              if (heatMap) {
                this.$cesiumPlus.heatMapManager.removeHeatMapById(layer.id)
              }
              const source = this.$cesiumPlus.primitiveManager.getBundle(layer.id)
              if (!source) {
                this.loadApiSourceLayer(layer, this.apiSourceDatas[config.dataSourceId])
              } else {
                source.updateStyle(markerStyle)
                source.enableCluster = markerStyle.isCluster
              }
            } else {
            // 热力图
              const source = this.$cesiumPlus.primitiveManager.getBundle(layer.id)
              if (source) {
                this.$cesiumPlus.primitiveManager.remvoeBundle(layer.id)
              }
              const config = JSON.parse(layer.config)
              this.loadApiSourceLayer(layer, this.apiSourceDatas[config.dataSourceId])
            }
          }
        } else if (layer.extendConfig && layer.extendConfig.isSppucData) {
          const markerStyle = markerStyles.adapterPointStyle(layer.pointStyle)
          const heatMap = this.$cesiumPlus.heatMapManager.getHeatMapById(layer.id)
          if (heatMap) {
            this.$cesiumPlus.heatMapManager.removeHeatMapById(layer.id)
          }
          if (markerStyle.type === 'billboard') {
            this.updateSppucMarker(layer.id)
          } else {
            this.updateSppucHeatMap(layer.id)
          }
        }
      }
      this.onCameraChanged()
      this.channel.push('', 'layerRefresh')
    },
    markerDatas (val) {
      this.isUpdateVisible = true
      if (val && this.layerList) {
        // this.loadMarkers()
        this.loadMarkerByLayer()
        // this.sampleLoad()
      }
    },
    apiSourceDatas (val) {
      if (val && this.layerList) {
        for (let i = 0; i < this.layerList.length; i++) {
          const layer = this.layerList[i]
          if (layer.dataSourceType === 'API') {
            const config = JSON.parse(layer.config)
            if (val[config.dataSourceId]) {
              this.loadApiSourceLayer(layer, val[config.dataSourceId])
            }
          }
        }
      }
    },
    defaultViewPoint (val) {
      if (val && val.view) {
        this.$cesiumPlus.cameraManager.flyTo({
          position: val.view.cameraPosition,
          direction: [val.view.cameraHeading, val.view.cameraPitch, val.view.cameraRoll]
        })
      }
    },
    frequentViewPoint (val) {
      this.channel.push(JSON.stringify(val), 'MapViewPointLoaded')
    },
    selectedMarker (val, oldVal) {
      if (oldVal) {
        const marker = this.$cesiumPlus.markerManager.getMarkerById(oldVal)
        if (marker) {
          marker.hover = false
          // marker.showLabel(false)
        }
      }
      if (val) {
        const marker = this.$cesiumPlus.markerManager.getMarkerById(val)
        if (marker) {
          marker.hover = true
          // marker.showLabel(true)
          this.showMarkerLabel(val)
          // 是否飞向标注点
          const layer = this.layerList.find((item) => { return item.id === marker.layerId })
          const isFlyTo = !(layer && layer.action && layer.action.flyTo === false)
          if (isFlyTo) {
            const cameraInfo = JSON.parse(marker.getProperties('camera_info') || '{}')
            if (cameraInfo && validateCameraInfo(cameraInfo)) {
              this.$cesiumPlus.cameraManager.flyTo({
                position: [cameraInfo.cameraLon, cameraInfo.cameraLat, cameraInfo.cameraHeight],
                direction: [cameraInfo.cameraHeading, cameraInfo.cameraPitch, cameraInfo.cameraRoll]
              })
            } else {
              const geoJson = JSON.parse(marker.toGeoJson())
              this.$cesiumPlus.cameraManager.zoomToGeometry(geoJson)
            }
          }
        }
      }
    }
  },
  data () {
    return {
      // 是否已加载模型数据
      isLoaded3dTile: false,
      // 鼠标当前进入entity id
      curHoverEntityId: undefined,
      // 当前选中标注
      curSelectedEntity: undefined,
      // 选中之前样式
      curSelectedStyle: undefined,
      // 模型id列表
      tilesetIdList: [],
      originHeight: {},
      // 图层显示状态
      layerVisibleState: {},
      // 已加载模型、影像列表
      loadedUrlList: [],
      customClickMarker: undefined,
      // 是否第一个模型
      isFirstModel: true
    }
  },
  created () {
    // 注册融合通信事件
    if (isSppuc && this.sppucAccount) {
      this.sppucManager = new SppucManager(this.sppucAccount || process.env.VUE_APP_SPPPUC_ACCOUNT, '#272829', {
        addDeviceCb: (deviceType, deviceId, data) => { this.onAddSppucDevice(deviceType, deviceId, data) },
        removeDeviceCb: (deviceType, deviceId, data) => { this.onRemoveSppucDevice(deviceType, deviceId, data) },
        updateDeviceCb: (deviceType, deviceId, data) => { this.onUpdateSppucDevice(deviceType, deviceId, data) }
      })
      window.sppucManager = this.sppucManager
    }
  },
  mounted () {
    this.initViewer()
    this.isFirstModel = true
    this.iframeMsgConnecter = new IframeMsgConnecter()
    this.iframeMsgConnecter.setApp(this)
  },
  destroyed () {
    if (this.loadMarkerTimer) {
      clearTimeout(this.loadMarkerTimer)
      this.loadMarkerTimer = null
    }
    if (this.$cesiumPlus) {
      this.$cesiumPlus.destroy()
      this.$cesiumPlus = null
      // this.$cesiumPlus.viewer.destroy()
      // this.$cesiumPlus.viewer = null
    }
    this.tilesetIdList = []

    if (this.iframeMsgConnecter) {
      this.iframeMsgConnecter.destroy()
      this.iframeMsgConnecter = null
    }
    this.isFirstModel = true
    if (isSppuc) {
      this.sppucManager.logout()
    }
  },
  methods: {
    initViewer () {
      Cesium.Camera.DEFAULT_VIEW_RECTANGLE = Cesium.Rectangle.fromDegrees(90, 10, 120, 70)
      const viewer = new Cesium.Viewer('cesiumViewer', {
        animation: false, // 隐藏动画器件
        timeline: false, // 隐藏时间轴
        geocoder: false, // 搜索按钮
        homeButton: false, // 主页按钮
        sceneModePicker: false,
        baseLayerPicker: false,
        navigationHelpButton: false,
        navigation: false,
        orderIndependentTranslucency: false, // 用来去掉地球表面的大气效果的黑圈问题
        contextOptions: {
          webgl: {
            alpha: true,
            preserveDrawingBuffer: true
          }
        },
        shouldAnimate: true,
        fullscreenButton: false,
        infoBox: false, // 点击实体不弹出信息框
        selectionIndicator: false, // 实体不被选中
        imageryProvider: undefined
      })
      // window.cesiumViewer = viewer
      const $cesiumPlus = new CesiumPlus(viewer)
      Vue.prototype.$cesiumPlus = $cesiumPlus
      Vue.prototype.$cesiumPlus.vueView = this
      Vue.prototype.$measureTools = new MeasureTools($cesiumPlus)

      this.channel.register({
        $cesiumPlus: this.$cesiumPlus,
        saveLayerVisible: this.saveLayerVisible
      })

      viewer.scene.debugShowFramesPerSecond = process.env.NODE_ENV === 'development' // 显示帧数
      viewer._cesiumWidget._creditContainer.style.display = 'none' // 隐藏版权信息
      viewer.scene.globe.enableLighting = false
      this.$cesiumPlus.statusBar.showStatusBar(process.env.NODE_ENV === 'development')
      // 设置鼠标事件
      viewer.cesiumWidget.screenSpaceEventHandler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK)

      // 开启深度监测
      viewer.scene.globe.depthTestAgainstTerrain = true
      // 天地图卫星影像
      this.$cesiumPlus.imageryManager.removeAllImageryLayer()
      this.$cesiumPlus.imageryManager.switchBaseLayer(CesiumPlus.LayerDefine.TianDiMapImgLayer)
      // 全局亮度
      this.setGlobalColor(1.2)
      // 地图亮度
      this.$cesiumPlus.imageryManager.getBaseLayer().brightness = 0.3

      // 天空盒
      const defaultSkyBox = viewer.scene.skyBox
      const currentSkyBox = new Cesium.GroundSkyBox({
        sources: {
          positiveX: require('@/assets/textures/skybox/east.jpg'),
          negativeX: require('@/assets/textures/skybox/west.jpg'),
          positiveY: require('@/assets/textures/skybox/north.jpg'),
          negativeY: require('@/assets/textures/skybox/south.jpg'),
          positiveZ: require('@/assets/textures/skybox/top.jpg'),
          negativeZ: require('@/assets/textures/skybox/bottom.jpg')
        }
      })
      // 控制高度，在小于2500米的时候显示近景图
      viewer.scene.postRender.addEventListener(() => {
        var e = viewer.camera.position
        if (Cesium.Cartographic.fromCartesian(e).height < 5000) {
          // 显示自定义的天空盒
          viewer.scene.skyBox = currentSkyBox
          viewer.scene.skyAtmosphere.show = false
        } else {
          viewer.scene.skyBox = defaultSkyBox
          viewer.scene.skyAtmosphere.show = true
        }
      })
      // 飞向默认视点
      if (this.defaultViewPoint && this.defaultViewPoint.view) {
        this.$cesiumPlus.cameraManager.flyTo({
          position: this.defaultViewPoint.view.cameraPosition,
          direction: [this.defaultViewPoint.view.cameraHeading, this.defaultViewPoint.view.cameraPitch, this.defaultViewPoint.view.cameraRoll]
        })
      }
      // 鼠标进入事件
      this._enterMarkerEvent = _.debounce(this.mouserEnterEvent.bind(this), 200)
      this.$cesiumPlus.viewer.scene.camera.moveEnd.addEventListener(this.onCameraChanged, this)
    },
    // 相机视角
    onCameraChanged () {
      const viewer = this.$cesiumPlus.viewer
      this.$cesiumPlus.markerManager.foreachMarker((id, marker) => {
        if (marker.boundingSphere && marker.boundingSphere.radius > 0) {
          const markerPos = marker.boundingSphere.center
          const cameraPos = viewer.scene.camera.position
          const distance = Cesium.Cartesian3.distance(markerPos, cameraPos)
          const maxDistance = marker.boundingSphere.radius * 10
          let isInDisplayViewer = distance < maxDistance
          const styleSet = this.getLayerStyleSet(marker.layerId)
          if (styleSet && styleSet.showLabelType === 'hide') {
            isInDisplayViewer = false
          } else if (styleSet && styleSet.showLabelType === 'show') {
            isInDisplayViewer = true
          }
          marker.isInDisplayViewer = isInDisplayViewer
          if (isInDisplayViewer) {
            this.showMarkerLabel(marker.id)
          } else {
            marker.showLabel(false)
          }
        } else {
          const markerPos = marker.getPosition()
          const cameraPos = viewer.scene.camera.position
          const distance = Cesium.Cartesian3.distance(markerPos, cameraPos)
          let isInDisplayViewer = distance < MAX_DISPLAY_DISTANCE
          const styleSet = this.getLayerStyleSet(marker.layerId)
          if (styleSet && styleSet.showLabelType === 'hide') {
            isInDisplayViewer = false
          } else if (styleSet && styleSet.showLabelType === 'show') {
            isInDisplayViewer = true
          }
          marker.isInDisplayViewer = isInDisplayViewer
          if (isInDisplayViewer) {
            this.showMarkerLabel(marker.id)
          } else {
            marker.showLabel(false)
          }
        }
      })
    },
    // 鼠标进入事件
    mouserEnterEvent (msg) {
      this.enterMarkerEvent(msg)
      this.enterPrimitive(msg)
    },
    enterPrimitive (msg) {
      const object = this.$cesiumPlus.viewer.scene.pick(msg.endPosition)
      if (object && object.collection && object.collection.layerId) {
        const bundle = this.$cesiumPlus.primitiveManager.getBundle(object.collection.layerId)
        if (!bundle || !object.primitive) {
          console.error('can not find bundle, ', object.collection.layerId)
          return
        }
        const layerId = object.collection.layerId
        const isHover = this.isLayerHover(layerId)
        if (isHover) {
          this.setPrimitiveHover(object.primitive)
        }
      } else {
        this.setPrimitiveHover(undefined)
      }
    },
    setPrimitiveHover (primitive) {
      if (this._hoverPrimitive && this._primitiveScale && this.selectedPrimitiveId !== this._hoverPrimitive.id) {
        this._hoverPrimitive.scale = this._primitiveScale
        this._hoverPrimitive = undefined
      }
      if (primitive) {
        this._hoverPrimitive = primitive
        this._primitiveScale = primitive.scale
        primitive.scale = 1.5
      }
    },
    enterMarkerEvent (event) {
      this.$cesiumPlus.markerManager.foreachMarker((entityId, marker) => {
        if (marker.isInDisplayViewer) {
          return
        }
        marker.showLabel(false)
      })
      const marker = this.$cesiumPlus.markerManager.pickMarker(event.endPosition)
      if (marker && marker.layerId !== 'poisearch' && marker.layerId !== 'route') {
        if (this._hoverMarkerId) {
          const selMarker = this.$cesiumPlus.markerManager.getMarkerById(this._hoverMarkerId)
          const isHover = !!this.isLayerHover(selMarker.layerId)
          if (isHover) {
            selMarker.hover = false
          }
          selMarker.showLabel(false)
          this._hoverMarkerId = undefined
        }

        const isHover = !!this.isLayerHover(marker.layerId)
        if (isHover) {
          marker.hover = true
        }
        this._hoverMarkerId = marker.id
        // marker.showLabel(true)
        this.showMarkerLabel(marker.id)
      } else {
        if (!this._hoverMarkerId) {
          return
        }
        const selMarker = this.$cesiumPlus.markerManager.getMarkerById(this._hoverMarkerId)
        const isHover = !!this.isLayerHover(selMarker.layerId)
        if (isHover) {
          selMarker.hover = false
        }
        this._hoverMarkerId = undefined
      }
    },
    // cesium事件
    onCesiumEvent (type, event) {
      if (type === Cesium.ScreenSpaceEventType.LEFT_CLICK) {
        this.onClickEvent(type, event)
      } else if (type === Cesium.ScreenSpaceEventType.MOUSE_MOVE) {
        this._enterMarkerEvent(event)
      } else if (type === CesiumPlus.CustomEvent.DrawState) {
        this.$store.dispatch('setDrawState', event)
      } else if (type === CesiumPlus.CustomEvent.MarkerPositionChanged) {
        this.$store.dispatch('setMarkerPositionEdit', event)
      }
    },
    // 保存图层显影状态
    saveLayerVisible (layerId, visible) {
      this.layerVisibleState[layerId] = visible
    },
    // 图层显示/隐藏更新
    updateLayerVisible () {
      treeForeach(this.layerTree.children, 'children', (item) => {
        if (item.type !== 'FOLDER') {
          const temp = this.layerVisibleState[item.id] !== false
          const visible = this.getLayerVisible(item) && temp
          this.$cesiumPlus.markerManager.showMarkerByLayer(item.id, visible)
          this.$cesiumPlus.heatMapManager.showHeatMapById(item.id, visible)
          this.$cesiumPlus.primitiveManager.showBundle(item.id, visible)
        }
      })
    },
    getLayerVisible (item) {
      let visible = this.layerVisible[item.clientId] !== false
      if (item.parentId) {
        const parentItems = searchBFS(this.layerTree.children, 'children', (childItem) => {
          return childItem.type === 'FOLDER' && childItem.id === item.parentId
        })
        if (parentItems && parentItems[0]) {
          visible = visible && this.getLayerVisible(parentItems[0])
        }
      }
      return visible
    },
    // 显示标注标题
    showMarkerLabel (markerId) {
      const marker = this.$cesiumPlus.markerManager.getMarkerById(markerId)
      if (!marker) {
        return
      }
      const layerId = marker.layerId
      this.updateMarkerLabel(layerId, markerId)
      marker.showLabel(true)
    },
    // 获取图层样式
    getLayerStyle (layerId) {
      for (let i = 0; i < this.layerList.length; i++) {
        if (this.layerList[i].id === layerId) {
          let style
          if (this.layerList[i].type === 'POINT') {
            style = markerStyles.adapterPointStyle(this.layerList[i].pointStyle)
          } else if (this.layerList[i].type === 'LINE') {
            style = markerStyles.adapterLineStyle(this.layerList[i].lineStyle)
          } else if (this.layerList[i].type === 'PLANE') {
            style = markerStyles.adapterPolygonStyle(this.layerList[i].planeStyle)
          }
          return style
        }
      }
    },
    // 获取图层样式设置
    getLayerStyleSet (layerId) {
      for (let i = 0; i < this.layerList.length; i++) {
        if (this.layerList[i].id === layerId) {
          let style
          if (this.layerList[i].type === 'POINT') {
            style = this.layerList[i].pointStyle
          } else if (this.layerList[i].type === 'LINE') {
            style = this.layerList[i].lineStyle
          } else if (this.layerList[i].type === 'PLANE') {
            style = this.layerList[i].planeStyle
          }
          return style
        }
      }
    },
    isLayerHover (layerId) {
      for (let i = 0; i < this.layerList.length; i++) {
        if (this.layerList[i].id === layerId) {
          if (this.layerList[i].type === 'POINT') {
            return this.layerList[i].pointStyle.isHover
          } else if (this.layerList[i].type === 'LINE') {
            return this.layerList[i].lineStyle.isHover
          } else if (this.layerList[i].type === 'PLANE') {
            return this.layerList[i].planeStyle.isHover
          }
          return false
        }
      }
    },
    // 从标注数据加载热力图
    createHeatMapFromMarkers (layerId, markers, style) {
      if (!markers || markers.length <= 0) {
        return
      }
      const positions = []
      const heatData = []
      for (let i = 0; i < markers.length; i++) {
        const longitude = markers[i].geoJson.geometry.coordinates[0]
        const latitude = markers[i].geoJson.geometry.coordinates[1]
        positions.push([longitude, latitude])
        heatData.push({
          x: longitude,
          y: latitude,
          value: 50
        })
      }
      const multPoint = turf.multiPoint(positions)
      const bbox = turf.bbox(multPoint)
      const squared = turf.square(bbox)
      const bounds = { west: squared[0], east: squared[2], south: squared[1], north: squared[3] }
      this.$cesiumPlus.heatMapManager.createHeatMap(layerId, {
        bounds: bounds,
        data: heatData,
        radius: style.radius,
        gradient: style.gradient
      })
    },
    // 从api数据创建热力图
    createHeatMapFromApiData (layerId, datas, style) {
      const positions = _.map(datas, item => {
        return [parseFloat(item.position[0]), parseFloat(item.position[1])]
      })
      const heatData = _.map(datas, item => {
        return {
          x: parseFloat(item.position[0]),
          y: parseFloat(item.position[1]),
          value: 50
        }
      })
      const multPoint = turf.multiPoint(positions)
      const bbox = turf.bbox(multPoint)
      const squared = turf.square(bbox)
      const bounds = { west: squared[0], east: squared[2], south: squared[1], north: squared[3] }
      this.$cesiumPlus.heatMapManager.createHeatMap(layerId, {
        bounds: bounds,
        data: heatData,
        radius: style.radius,
        gradient: style.gradient
      })
    },
    sampleLoad () {
      console.log('-------------------')
      for (let i = 0; i < this.markerDatas.length; i++) {
        const geoJsons = {
          'type': 'FeatureCollection',
          'features': [

          ],
          'layerId': this.markerDatas[i].id
        }
        // const layerId = this.markerDatas[i].id
        // const style = this.getLayerStyle(layerId)
        const markers = this.markerDatas[i].marks
        let index = 0
        markers.forEach(item => {
          item.geoJson.id = item.id
          item.geoJson.layerId = item.layerId
          item.geoJson.properties.markerName = item.name
          item.geoJson.properties.index = index.toString()
          const marker = this.$cesiumPlus.markerManager.getMarkerById(item.id)
          if (marker) {
            // marker.setProperties(item.geoJson.properties)
            // this.updateMarkerStyle(item.layerId, item.id, style)
            // this.updateMarkerLabel(item.layerId, item.id)
            return
          }
          geoJsons.features.push(item.geoJson)
          index++
        })
        this.isLoaded = true
        // this.$cesiumPlus.markerManager.addMarkerFromGeoJson(geoJsons, [style])
        Cesium.GeoJsonDataSource.load(geoJsons, { clampToGround: true }).then(geoDataSource => {
          this.$cesiumPlus.viewer.dataSources.add(geoDataSource)
        })
      }
    },
    // 采样点位高度
    async samplePositionHeight (markerDatas) {
      const promiseList = []
      markerDatas.forEach(data => {
        promiseList.push(
          // eslint-disable-next-line promise/param-names
          new Promise((reslove) => {
            const point = new Cesium.Cartographic(
              Cesium.Math.toRadians(parseFloat(data.position[0])),
              Cesium.Math.toRadians(parseFloat(data.position[1]))
            )
            this.$cesiumPlus.viewer.scene.sampleHeightMostDetailed([point]).then(position => {
              data.position[2] = position[0].height ? position[0].height : 0
              reslove(data)
            })
          })
        )
      })
      await Promise.all(promiseList)
      return markerDatas
    },
    async loadApiSourceLayer (layer, apiData) {
      if (layer.dataSourceType === 'API') {
        const config = JSON.parse(layer.config)
        const datas = dataset2table(apiData)
        const markerDatas = _.map(datas, item => {
          return {
            id: item[config.markerId],
            position: coordTransform(config.coordinatesType, [
              item[config.lonHeader],
              item[config.latHeader],
              item[config.heightHeader]
            ])
          }
        })
        // 高度自动采样
        if (config.extendDatas && config.extendDatas.autoHeight) {
          await this.samplePositionHeight(markerDatas)
        }

        const markerStyle = markerStyles.adapterPointStyle(layer.pointStyle)
        // 散点图
        if (markerStyle.type === 'billboard') {
          const source = this.$cesiumPlus.primitiveManager.getBundle(layer.id)
          if (!source) {
            this.$cesiumPlus.primitiveManager.createBillboardBundle({
              id: layer.id,
              style: markerStyle,
              datas: markerDatas,
              cluster: markerStyle.isCluster
            })
          } else {
            const delIds = _.without(Object.keys(source.billboardMap), ..._.map(markerDatas, item => {
              return `billboard-${layer.id}-${item.id}`
            }))
            // 删除的数据
            delIds.forEach(item => {
              source.remove(item)
            })
            source.updateDatas(markerDatas)
          }
        } else {
          // 热力图
          const heatMap = this.$cesiumPlus.heatMapManager.getHeatMapById(layer.id)
          if (heatMap) {
            this.$cesiumPlus.heatMapManager.removeHeatMapById(layer.id)
          }
          this.createHeatMapFromApiData(layer.id, markerDatas, markerStyle)
          const bundle = this.$cesiumPlus.primitiveManager.getBundle(layer.id)
          if (bundle) {
            this.$cesiumPlus.primitiveManager.remvoeBundle(layer.id)
          }
        }
        setTimeout(() => {
          this.updateLayerVisible()
        }, 20)
      }
    },
    loadMarkerByLayer () {
      for (let i = 0; i < this.markerDatas.length; i++) {
        if (this.markerDatas[i].dataSourceType === 'API') {
          continue
        }
        if (this.markerDatas[i].extendConfig && this.markerDatas[i].extendConfig.isSppucData) {
          continue
        }
        if (this.markerDatas[i].module === 'sppuc') { // 融合通信图层每次修改id会变化，移除图层重新创建
          this.$cesiumPlus.markerManager.removeMarkerByLayer(this.markerDatas[i].id)
        }
        const geoJsons = {
          'type': 'FeatureCollection',
          'features': [

          ],
          'layerId': this.markerDatas[i].id
        }
        // let startTime = Date.now()
        const layerId = this.markerDatas[i].id
        const style = this.getLayerStyle(layerId)
        const markers = this.markerDatas[i].marks ?? []
        if (style.type === 'heatMap') { // 热力图
          const heatMap = this.$cesiumPlus.heatMapManager.getHeatMapById(layerId)
          if (heatMap) {
            this.$cesiumPlus.heatMapManager.removeHeatMapById(layerId)
          }
          this.createHeatMapFromMarkers(layerId, markers, style)
          const dataSource = this.$cesiumPlus.markerManager.getDataSourceByLayer(layerId)
          if (dataSource) {
            this.$cesiumPlus.markerManager.removeMarkerByLayer(layerId)
          }
          continue
        } else if (style.type === 'billboard') { // 散点图
          const heatMap = this.$cesiumPlus.heatMapManager.getHeatMapById(layerId)
          if (heatMap) {
            this.$cesiumPlus.heatMapManager.removeHeatMapById(layerId)
          }
        }
        let index = 0
        markers.forEach(item => {
          item.geoJson.id = item.id
          item.geoJson.layerId = item.layerId
          item.geoJson.properties.markerName = item.name
          item.geoJson.properties.index = index.toString()
          const marker = this.$cesiumPlus.markerManager.getMarkerById(item.id)
          if (marker) {
            marker.setProperties(item.geoJson.properties)
            this.updateMarkerStyle(item.layerId, item.id, style)
            return
          }
          geoJsons.features.push(item.geoJson)
          index++
        })
        // console.log(new Date().toLocaleString(), 'begin load----------------', Date.now() - startTime)
        // startTime = Date.now()
        this.$cesiumPlus.markerManager.addMarkerFromGeoJson(geoJsons, [style]).then(markers => {
          // console.log(new Date().toLocaleString(), 'load finish----------------', Date.now() - startTime)
          // startTime = Date.now()
          markers.forEach(marker => {
            this.updateMarkerStyle(marker.layerId, marker.id, style)
          })
          // console.log(new Date().toLocaleString(), 'set style----------------', Date.now() - startTime)
          // startTime = Date.now()
          if (style.type === 'billboard') {
            this.$cesiumPlus.markerManager.clusterIcon(layerId, {
              enable: style.isCluster
            })
          }
        })
        // this.$cesiumPlus.markerManager.addGeoDataSources(layerId, geoJsons, style)

        if (this.isUpdateVisible) {
          setTimeout(() => {
            this.updateLayerVisible()
          }, 20)
          this.isUpdateVisible = false
        }
      }
    },
    loadPolygonPrimitive (markers, layerId) {
      const markerPositions = markers.map(item => {
        return {
          id: item.id,
          positions: item.geoJson.geometry.coordinates[0].flat(),
          isHaveHeight: item.geoJson.geometry.coordinates[0][0].length >= 3
        }
      })
      this.$cesiumPlus.primitiveManager.createGroundPolygonBundle({
        datas: markerPositions,
        layerId: layerId
      })
    },
    // 加载标注
    loadMarkers () {
      for (let i = 0; i < this.markerDatas.length; i++) {
        const markers = this.markerDatas[i].marks
        const layerId = this.markerDatas[i].id
        const style = this.getLayerStyle(layerId)
        if (style.type === 'heatMap') { // 热力图
          const heatMap = this.$cesiumPlus.heatMapManager.getHeatMapById(layerId)
          if (heatMap) {
            this.$cesiumPlus.heatMapManager.removeHeatMapById(layerId)
          }
          this.createHeatMapFromMarkers(layerId, markers, style)
          const dataSource = this.$cesiumPlus.markerManager.getDataSourceByLayer(layerId)
          if (dataSource) {
            this.$cesiumPlus.markerManager.removeMarkerByLayer(layerId)
          }
        } else if (style.type === 'billboard') { // 散点图
          const heatMap = this.$cesiumPlus.heatMapManager.getHeatMapById(layerId)
          if (heatMap) {
            this.$cesiumPlus.heatMapManager.removeHeatMapById(layerId)
          }
          // for (let j = 0; j < markers.length; j++) {
          //   this.loadMarker(markers[j])
          // }
          this.loadMarkerByTick(markers)

          this.$nextTick(() => {
            // 聚合
            if (style.type === 'billboard') {
              this.$cesiumPlus.markerManager.clusterIcon(layerId, {
                enable: style.isCluster
              })
            }
          })
        } else {
          // for (let j = 0; j < markers.length; j++) {
          //   this.loadMarker(markers[j])
          // }
          this.loadMarkerByTick(markers)
        }
        if (this.isUpdateVisible) {
          setTimeout(() => {
            this.updateLayerVisible()
          }, 20)
          this.isUpdateVisible = false
        }
      }
    },
    // 按帧加载
    loadMarkerByTick (markers) {
      let index = 0
      const self = this
      function loadMarker (i) {
        self.loadMarkerTimer = setTimeout(() => {
          const data = markers[i]
          if (!data || !data.geoJson) {
            return
          }
          const layerId = data.layerId
          const style = self.getLayerStyle(layerId)
          if (style.type === 'heatMap') {
            return
          }
          const marker = self.$cesiumPlus.markerManager.getMarkerById(data.id)
          if (!marker) {
            self.markerIndex++
            data.geoJson.id = data.id
            data.geoJson.layerId = data.layerId
            data.geoJson.properties.markerName = data.name
            data.geoJson.properties.index = self.markerIndex.toString()
            self.$cesiumPlus.markerManager.addMarkerFromGeoJson(data.geoJson, [style]).then((mark) => {
              self.updateMarkerStyle(data.layerId, data.id, style)
            })
          } else {
            // 更新自定义属性
            data.geoJson.properties.markerName = data.name
            marker.setProperties(data.geoJson.properties)
            self.updateMarkerStyle(data.layerId, data.id, style)
          }
          self.$nextTick(() => {
            // 聚合
            if (style.type === 'billboard') {
              self.$cesiumPlus.markerManager.clusterIcon(layerId, {
                enable: style.isCluster
              })
            }
          })
          index++
          if (index < markers.length) {
            loadMarker(index)
          } else {
            self.loadMarkerTimer = null
          }
        }, 1)
      }
      loadMarker(index)
    },
    // 加载标注
    loadMarker (data) {
      if (!data.geoJson) {
        return
      }
      const layerId = data.layerId
      const style = this.getLayerStyle(layerId)
      const marker = this.$cesiumPlus.markerManager.getMarkerById(data.id)
      if (!marker) {
        this.markerIndex++
        data.geoJson.id = data.id
        data.geoJson.layerId = data.layerId
        data.geoJson.properties.markerName = data.name
        data.geoJson.properties.index = this.markerIndex.toString()
        this.$cesiumPlus.markerManager.addMarkerFromGeoJson(data.geoJson, [style]).then((mark) => {
          this.updateMarkerStyle(data.layerId, data.id, style)
        })
      } else {
        // 更新自定义属性
        data.geoJson.properties.markerName = data.name
        marker.setProperties(data.geoJson.properties)
        this.updateMarkerStyle(data.layerId, data.id, style)
      }
    },
    // 更新标注样式
    updateMarkerStyle (layerId, markerId, style) {
      const marker = this.$cesiumPlus.markerManager.getMarkerById(markerId)
      if (!marker) {
        return
      }
      if (!marker.show && style.type === 'heatMap') {
        return
      }
      if (style.type === 'wall') {
        if (!marker.entity.wall) {
          const _positions = marker.entity.polyline.positions
          marker.entity.wall = new Cesium.WallGraphics({
            show: true,
            positions: _positions
          })
          marker.entity.polyline = undefined
        }
      } else if (style.type === 'polyline') {
        if (!marker.entity.polyline) {
          const _positions = marker.entity.wall.positions
          marker.entity.polyline = new Cesium.PolylineGraphics({
            show: true,
            positions: _positions
          })
          marker.entity.wall = undefined
        }
      }
      marker.setStyle([style])
      // this.$nextTick(() => {
      //   // 聚合
      //   if (style.type === 'billboard') {
      //     this.$cesiumPlus.markerManager.clusterIcon(layerId, {
      //       enable: style.isCluster
      //     })
      //   }
      // })
      // 标签销毁资源太大，开启聚合不加载标签
      // if (!style.isCluster) {
      //   this.updateMarkerLabel(layerId, markerId)
      // }
      // this.updateMarkerLabel(layerId, markerId)
    },
    // 更新图层标签
    updateMarkerLabel (layerId, markerId) {
      let label
      let originPropertyId
      let type = 'TEXT'
      let options
      let labelStyle
      for (let i = 0; i < this.layerList.length; i++) {
        if (this.layerList[i].id === layerId) {
          const layer = this.layerList[i]
          if (this.layerList[i].type === 'POINT') {
            label = this.layerList[i].pointStyle.label
            labelStyle = this.layerList[i].pointStyle.labelStyle
          } else if (this.layerList[i].type === 'LINE') {
            label = this.layerList[i].lineStyle.label
            labelStyle = this.layerList[i].lineStyle.labelStyle
          } else if (this.layerList[i].type === 'PLANE') {
            label = this.layerList[i].planeStyle.label
            labelStyle = this.layerList[i].planeStyle.labelStyle
          }
          for (let j = 0; j < layer.properties.length; j++) {
            if (layer.properties[j].id.toString() === label) {
              originPropertyId = layer.properties[j].originPropertyId
              type = layer.properties[j].type
              options = layer.properties[j].options
            }
          }
        }
      }
      const marker = this.$cesiumPlus.markerManager.getMarkerById(markerId)
      marker.setLabelFromProperty(label, originPropertyId, type, options, labelStyle)
    },
    // 鼠标进入marker，显示marker名称
    onEnterMarker (type, event) {
      if (type === Cesium.ScreenSpaceEventType.MOUSE_MOVE) {
        this._enterMarkerEvent(event)
      }
    },
    onClickEvent (event, msg) {
      if (this.$cesiumPlus.drawHandler.isDrawState === true) {
        return
      }
      if (this.$cesiumPlus.canvasPicker.enabled === true) {
        return
      }
      if (this.isInMarkerEditing) {
        return
      }
      const marker = this.$cesiumPlus.markerManager.pickMarker(msg.position)
      if (marker) {
        if (this.customClickMarker) {
          this.customClickMarker(marker)
        } else {
          this.onClickMarker(marker)
        }
      } else {
        const object = this.$cesiumPlus.viewer.scene.pick(msg.position)
        if (object && object.collection && object.collection.layerId) {
          if (this.customClickMarker) {
            this.customClickMarker(object)
          } else {
            this.onClickPrimitive(object)
          }
        } else if (object && object.id && object.id instanceof Cesium.Entity) {
          this.onClickEntity(object)
        } else {
          this.$store.dispatch('selectMarker', undefined)
          this.$emit('selectPrimitive', undefined)
        }
      }
    },
    // 鼠标点击marker
    onClickMarker (marker, eventData) {
      const layer = this.layerList.find((item) => { return item.id === marker.layerId })
      // 具备自定义点击maker回调函数时，仅支持回调函数
      if (this.customClickMarker) {
        this.customClickMarker(marker)
      } else if (marker.layerId === 'poisearch') {
        const markerId = marker.id
        this.$emit('cesiumMsg', 'selectPoiMarker', markerId)
      } else if (marker.layerId === 'markerSearch') {
        const originLayerId = marker.getProperties('originLayerId')
        const originLayer = this.layerList.find((item) => { return item.id === originLayerId })
        // 搜索结果为融合通信数据
        if (originLayer.module === 'sppuc') {
          this.onClickSppucDevice({
            layer: originLayer,
            deviceId: marker.getProperties('deviceId'),
            sppucMarkerId: marker.id,
            name: marker.name,
            eventData
          })
        } else {
          const markerId = marker.id.replace('markerSearch-', '')
          this.$store.dispatch('selectMarker', markerId)
        }
      } else if (layer.module === 'sppuc') {
        this.onClickSppucDevice({
          layer,
          deviceId: marker.getProperties('deviceId'),
          sppucMarkerId: marker.id,
          name: marker.name,
          eventData
        })
      } else {
        const markerId = marker.id
        // if (!this.uiHiddenState.isHiddenMarker) {

        // }
        this.$store.dispatch('selectMarker', markerId)
        this.onClickMarkerMsg(marker)
      }
    },
    onClickMarkerMsg (marker) {
      const layerId = marker.layerId
      for (let i = 0; i < this.markerDatas.length; i++) {
        if (this.markerDatas[i].id === layerId) {
          const layer = this.markerDatas[i]
          const markers = layer.marks
          const layerProperties = layer.properties || []
          markers.forEach(item => {
            if (item.id === marker.id) {
              const markerProperties = item.geoJson.properties
              const data = {}
              layerProperties.forEach(property => {
                const name = property.name
                const value = markerProperties[property.id] || markerProperties[property.originPropertyId]
                data[name] = value
              })
              data.markerId = marker.id
              data.markerName = marker.name
              data.layerId = marker.layerId
              // console.log('--------', data)
              this.iframeMsgConnecter.postMessage({
                type: 'clickMarker',
                data
              })
            }
          })
        }
      }
    },
    onClickPrimitiveMsg ({ layerId, id }) {
      for (let i = 0; i < this.layerList.length; i++) {
        if (this.layerList[i].id === layerId) {
          const layer = this.layerList[i]
          const config = JSON.parse(layer.config)
          // const tableProperties = config.tableProperties

          const datas = dataset2table(this.apiSourceDatas[config.dataSourceId])
          const markerData = datas.find(item => {
            return item[config.markerId] === id
          })
          markerData.markerId = `billboard-${layerId}-${id}`
          this.iframeMsgConnecter.postMessage({
            type: 'clickMarker',
            markerData
          })
          // console.log('this.apiSourceDatas---------', tableProperties, markerData)
        }
      }
    },
    // 点击entity
    onClickEntity (object) {
      if (object !== undefined && object.id !== undefined && object.id instanceof Cesium.Entity) {
        const entity = object.id
        const properties = entity.properties
        if (properties) {
          const values = properties.getValue(Cesium.JulianDate.now())
          // 测量entity，移除
          if (values.measureClose && object.primitive instanceof Cesium.Billboard) {
            this.$cesiumPlus.markerManager.removeMarkerById(entity.id)
            // 清除可视域分析
            if (values.isviewShed3D) {
              this.$cesiumPlus.viewShed3D.clear()
              this.$cesiumPlus.viewShed3D.remove()
              this.$store.commit('SELECT_SHOWVIEWSHED3DBOX', false)
              this.$store.commit('SELECT_VIEWSHED3DDATA', null)
            }
            // 关闭可视域分析弹窗
            if (values.sapceTool) {
              this.$store.commit('SELECT_ISSHOWSPACETOOL', true)
              this.$store.commit('SELECT_SPACESEARCHJEOJSON', null)
            }
            this.$store.commit('SET_DRAWSTATE', false)
          }
        } else {
          if (!entity._name) {
            console.log('关闭通视')
            this.$measureTools.clearAnalyzeEntities()
            // this.$store.commit('SET_DRAWSTATE', false)
          }
        }
      }
    },
    // 点击图元
    onClickPrimitive (object) {
      if (object && object.collection && object.collection.layerId) {
        const bundle = this.$cesiumPlus.primitiveManager.getBundle(object.collection.layerId)
        if (!bundle || !object.primitive) {
          console.error('can not find bundle, ', object.collection.layerId)
          return
        }
        // if (!this.uiHiddenState.isHiddenMarker) {

        // }
        this.$emit('selectPrimitive', {
          layerId: object.collection.layerId,
          id: object.primitive.dataId
        })
        this.onClickPrimitiveMsg({
          layerId: object.collection.layerId,
          id: object.primitive.dataId
        })
      }
    },
    // 加载服务器资源
    loadServerRes (url, options) {
      if (this.loadedUrlList.includes(url)) {
        return
      }
      if (options.type === 'THREE_DIMENSIONS') {
        this.loadServer3Dtiles(url, options)
      } else if (options.type === 'TWO_DIMENSIONS') {
        this.loadServerImagery(url, options)
      }
      this.loadedUrlList.push(url)
    },
    // 加载二维影像
    loadServerImagery (url, options) {
      let tilingScheme = new Cesium.WebMercatorTilingScheme()
      if (options.tilingScheme === 'GeographicTilingScheme') {
        tilingScheme = new Cesium.GeographicTilingScheme()
      }
      if (options.tilingScheme === 'AmapMercatorTilingScheme') {
        tilingScheme = new Cesium.AmapMercatorTilingScheme()
      }
      this.$cesiumPlus.imageryManager.addUrlTemplateImagery({
        url: url,
        tilingScheme: tilingScheme
      })
    },
    // 加载服务器模型资源
    loadServer3Dtiles (url, options) {
      const self = this
      const isFlyTo = !this.defaultViewPoint && this.isFirstModel
      const height = options.height + this.modelHeight
      this.$cesiumPlus.cesium3DTilesetManager.add3dTileset(url, {
        ...options,
        isFlyTo: isFlyTo,
        height: options.height ? options.height : 0,
        loaded: (setId, tileset) => {
          self.tilesetIdList.push(setId)
          this.change3dTilesetHeight(setId, height)
          self.originHeight[setId] = options.height
        }
      })
      this.isFirstModel = false
    },
    // 改变模型高度
    change3dTilesetHeight (setId, height) {
      this.$cesiumPlus.cesium3DTilesetManager.changeHeight(setId, height)
    },
    changeAll3dTilesetHeight (height) {
      for (let i = 0; i < this.tilesetIdList.length; i++) {
        const setId = this.tilesetIdList[i]
        this.change3dTilesetHeight(this.tilesetIdList[i], this.originHeight[setId] + height)
      }
    },
    // 设置全局颜色
    setGlobalColor (brightness) {
      const brightnessStage = Cesium.PostProcessStageLibrary.createBrightnessStage()
      brightnessStage.uniforms.brightness = brightness
      Vue.prototype.$brightnessStage = brightnessStage
      this.$cesiumPlus.viewer.scene.postProcessStages.add(brightnessStage)
    },
    /// //////////////////////////////////////////////////// sppuc 设备功能 start ///////////////////////////////////////////////////////
    // 更新sppuc设备热力图
    updateSppucHeatMap (layerId) {
      // 热力图
      const layer = this.sppucLayers.find((item) => { return item.id === layerId })
      const markerStyle = markerStyles.adapterPointStyle(layer.pointStyle)
      const deviceType = layer.extendConfig.deviceType
      const markerDatas = this.sppucManager.getDevicesOl(deviceType)
      const heatMap = this.$cesiumPlus.heatMapManager.getHeatMapById(layerId)
      if (heatMap) {
        this.$cesiumPlus.heatMapManager.removeHeatMapById(layerId)
      }
      this.createHeatMapFromApiData(layerId, markerDatas, markerStyle)
      const bundle = this.$cesiumPlus.primitiveManager.getBundle(layerId)
      if (bundle) {
        this.$cesiumPlus.primitiveManager.remvoeBundle(layerId)
      }
      this.$cesiumPlus.markerManager.removeMarkerByLayer(layerId)
    },
    // 更新sppuc设备标注 Primitive 方式
    async updateSppucPrimitive (layerId) {
      const layer = this.sppucLayers.find((item) => { return item.id === layerId })
      const markerStyle = markerStyles.adapterPointStyle(layer.pointStyle)
      const deviceType = layer.extendConfig.deviceType
      const datas = this.sppucManager.getDevicesOl(deviceType)
      if (layer.pointStyle.isAutoHeight === true) {
        await this.samplePositionHeight(datas)
      }
      const source = this.$cesiumPlus.primitiveManager.getBundle(layerId)
      if (source) {
        source.updateStyle(markerStyle)
        source.updateDatas(datas)
      } else {
        this.$cesiumPlus.primitiveManager.createBillboardBundle({
          id: layer.id,
          style: markerStyle,
          datas: datas,
          cluster: markerStyle.isCluster
        })
      }
    },
    // 更新sppuc设备标注 entity 方式
    async updateSppucMarker (layerId) {
      if (!this.sppucManager) {
        return
      }
      const dataSource = this.$cesiumPlus.markerManager.getDataSourceByLayer(layerId)
      const visible = dataSource ? dataSource.show : false
      const layer = this.sppucLayers.find((item) => { return item.id === layerId })
      const markerStyle = markerStyles.adapterPointStyle(layer.pointStyle)
      markerStyle._disableDepthTestDistance = Number.POSITIVE_INFINITY
      const deviceType = layer.extendConfig.deviceType
      const datas = this.sppucManager.getDevicesOl(deviceType)
      if (layer.pointStyle.isAutoHeight === true) {
        await this.samplePositionHeight(datas)
      }
      datas.forEach(item => {
        const markerId = `sppuc-${layer.id}-${item.id}`
        const marker = this.$cesiumPlus.markerManager.getMarkerById(markerId)
        if (marker) {
          marker.setPosition(item.position)
          marker.setStyle([markerStyle])
        }
      })
      // const visible = this.getLayerIdVisible(layerId)
      this.$cesiumPlus.markerManager.showMarkerByLayer(layerId, visible)
    },
    // sppuc添加设备
    async onAddSppucDevice (deviceType, deviceId, data) {
      const markerDatas = [{
        id: deviceId,
        name: data[4] || '',
        position: [data[0], data[1], 40]
      }]
      for (let i = 0; i < this.sppucLayers.length; i++) {
        const layer = this.sppucLayers[i]
        const layerDeviceType = this.sppucLayers[i].extendConfig.deviceType.toString()
        if (layerDeviceType !== deviceType.toString() || !layer.extendConfig.isSppucData) {
          continue
        }
        const dataSource = this.$cesiumPlus.markerManager.getDataSourceByLayer(layer.id)
        const visible = dataSource ? dataSource.show : true
        const markerStyle = markerStyles.adapterPointStyle(layer.pointStyle)
        // 散点图
        if (markerStyle.type === 'billboard') {
          markerStyle._disableDepthTestDistance = Number.POSITIVE_INFINITY
          // 高度自动采样
          if (layer.pointStyle.isAutoHeight === true) {
            await this.samplePositionHeight(markerDatas)
          }
          // 原型方式创建
          // const source = this.$cesiumPlus.primitiveManager.getBundle(layer.id)
          // if (!source) {
          //   this.$cesiumPlus.primitiveManager.createBillboardBundle({
          //     id: layer.id,
          //     style: markerStyle,
          //     datas: markerDatas,
          //     cluster: markerStyle.isCluster
          //   })
          // } else {
          //   source.add(markerDatas[0])
          // }
          // entity方式创建
          this.$cesiumPlus.markerManager.createMarker({
            layerId: layer.id,
            name: markerDatas[0].name,
            position: markerDatas[0].position,
            id: `sppuc-${layer.id}-${markerDatas[0].id}`,
            properties: {
              deviceId: markerDatas[0].id
            },
            billboard: {
              show: true
            },
            style: [markerStyle]
          })
        } else {
          this.updateSppucHeatMap(layer.id)
        }
        // const visible = this.getLayerIdVisible(layer.id)
        this.$cesiumPlus.markerManager.showMarkerByLayer(layer.id, visible)
      }
      this.$store.dispatch('sppucDeviceUpdate', {
        type: 'add',
        deviceType,
        deviceId,
        data
      })
    },
    // sppuc移除设备
    onRemoveSppucDevice (deviceType, deviceId, data) {
      for (let i = 0; i < this.sppucLayers.length; i++) {
        const layer = this.sppucLayers[i]
        const layerDeviceType = this.sppucLayers[i].extendConfig.deviceType.toString()
        if (layerDeviceType !== deviceType || !layer.extendConfig.isSppucData) {
          continue
        }
        const markerStyle = markerStyles.adapterPointStyle(layer.pointStyle)
        // 散点图
        if (markerStyle.type === 'billboard') {
          // 原型方式创建
          // const source = this.$cesiumPlus.primitiveManager.getBundle(layer.id)
          // if (source) {
          //   source.remove(deviceId)
          // }
          // entity方式创建
          this.$cesiumPlus.markerManager.removeMarkerById(`sppuc-${layer.id}-${deviceId}`)
        } else {
          this.updateSppucHeatMap(layer.id)
        }
      }
      this.$store.dispatch('sppucDeviceUpdate', {
        type: 'remove',
        deviceType,
        deviceId,
        data
      })
    },
    // sppuc更新设备
    async onUpdateSppucDevice (deviceType, deviceId, data) {
      const markerDatas = [{
        id: deviceId,
        name: data[4] || '',
        position: [data[0], data[1], 20]
      }]
      for (let i = 0; i < this.sppucLayers.length; i++) {
        const layer = this.sppucLayers[i]
        const layerDeviceType = this.sppucLayers[i].extendConfig.deviceType.toString()
        if (layerDeviceType !== deviceType || !layer.extendConfig.isSppucData) {
          continue
        }
        const dataSource = this.$cesiumPlus.markerManager.getDataSourceByLayer(layer.id)
        const visible = dataSource ? dataSource.show : false
        const markerStyle = markerStyles.adapterPointStyle(layer.pointStyle)
        // 散点图
        const heatMap = this.$cesiumPlus.heatMapManager.getHeatMapById(layer.id)
        if (heatMap) {
          this.$cesiumPlus.heatMapManager.removeHeatMapById(layer.id)
        }
        if (markerStyle.type === 'billboard') {
          markerStyle._disableDepthTestDistance = Number.POSITIVE_INFINITY
          // 高度自动采样, 频繁更新消耗性能，暂不更新
          // if (layer.pointStyle.isAutoHeight === true) {
          //   await this.samplePositionHeight(markerDatas)
          // }

          // 原型方式创建
          // const source = this.$cesiumPlus.primitiveManager.getBundle(layer.id)
          // if (source) {
          //   if (!source.getBillboard(markerDatas[0].id)) {
          //     source.add(markerDatas[0])
          //   } else {
          //     source.updatePosition(markerDatas[0].id, markerDatas[0].position)
          //   }
          // } else {
          //   // console.error('have no sppuc layer id = ', layer.id)
          //   this.$cesiumPlus.primitiveManager.createBillboardBundle({
          //     id: layer.id,
          //     style: markerStyle,
          //     datas: markerDatas,
          //     cluster: markerStyle.isCluster
          //   })
          // }
          // entity方式创建
          const markerId = `sppuc-${layer.id}-${markerDatas[0].id}`
          const marker = this.$cesiumPlus.markerManager.getMarkerById(markerId)
          if (marker) {
            const dataSource = this.$cesiumPlus.markerManager.getDataSourceByLayer(layer.id)
            if (dataSource && dataSource.show) {
              marker.setPosition(markerDatas[0].position)
            }
          } else {
            this.$cesiumPlus.markerManager.createMarker({
              layerId: layer.id,
              name: markerDatas[0].name,
              position: markerDatas[0].position,
              id: markerId,
              properties: {
                deviceId: markerDatas[0].id
              },
              billboard: {
                show: true
              },
              style: [markerStyle]
            })
          }
        } else {
          this.updateSppucHeatMap(layer.id)
        }
        // const visible = this.getLayerIdVisible(layer.id)
        this.$cesiumPlus.markerManager.showMarkerByLayer(layer.id, visible)
      }
      this.$store.dispatch('sppucDeviceUpdate', {
        type: 'update',
        deviceType,
        deviceId,
        data
      })
    },
    // 点击sppuc设备
    onClickSppucDevice ({ layer, deviceId, name, sppucMarkerId, eventData }) {
      const isFlyTo = !(layer && layer.action && layer.action.flyTo === false)
      if (isFlyTo) {
        // 原型方式创建
        // if (layer.extendConfig.isSppucData) {
        //   const bundle = this.$cesiumPlus.primitiveManager.getBundle(layer.id)
        //   const billboard = bundle.getBillboard(deviceId)
        //   if (billboard) {
        //     this.$cesiumPlus.cameraManager.zoomToPosition(billboard.position)
        //   }
        // } else {
        //   this.$cesiumPlus.markerManager.foreachMarker((id, marker) => {
        //     if (marker.layerId !== layer.id) {
        //       return
        //     }
        //     const did = marker.getProperties('deviceId')
        //     if (did === deviceId) {
        //       const cameraInfo = JSON.parse(marker.getProperties('camera_info') || '{}')
        //       if (cameraInfo && validateCameraInfo(cameraInfo)) {
        //         this.$cesiumPlus.cameraManager.flyTo({
        //           position: [cameraInfo.cameraLon, cameraInfo.cameraLat, cameraInfo.cameraHeight],
        //           direction: [cameraInfo.cameraHeading, cameraInfo.cameraPitch, cameraInfo.cameraRoll]
        //         })
        //       } else {
        //         const geoJson = JSON.parse(marker.toGeoJson())
        //         this.$cesiumPlus.cameraManager.zoomToGeometry(geoJson)
        //       }
        //     }
        //   })
        // }
        // entity方式创建
        let markerId = sppucMarkerId
        if (!markerId) {
          markerId = `sppuc-${layer.id}-${deviceId}`
        }
        const marker = this.$cesiumPlus.markerManager.getMarkerById(markerId)
        if (marker) {
          const cameraInfo = JSON.parse(marker.getProperties('camera_info') || '{}')
          if (cameraInfo && validateCameraInfo(cameraInfo)) {
            this.$cesiumPlus.cameraManager.flyTo({
              position: [cameraInfo.cameraLon, cameraInfo.cameraLat, cameraInfo.cameraHeight],
              direction: [cameraInfo.cameraHeading, cameraInfo.cameraPitch, cameraInfo.cameraRoll]
            })
          } else {
            const geoJson = JSON.parse(marker.toGeoJson())
            this.$cesiumPlus.cameraManager.zoomToGeometry(geoJson)
          }
        }
      }

      const isUserWin = false // 是否使用弹出新窗口方式打开融合通信设备
      if (isUserWin) {
        this.sppucManager.openDeviceWin(deviceId, layer.extendConfig.deviceType)
      } else {
        if (isTw(layer.extendConfig.deviceType)) {
          this.$emit('showSppucTw', {
            type: layer.extendConfig.deviceType,
            name,
            deviceId
          })
        } else {
          const left = 770
          const top = 300
          this.sppucManager.rdCard(deviceId, layer.extendConfig.deviceType, `${left}px`, `${top}px`)
          this.$store.dispatch('setDrawState', true)
          // 拦截融合通信弹窗关闭事件
          const element = document.getElementById(`ppp${deviceId}`)
          if (element) {
            const closeBtn = element.querySelector('.close')
            closeBtn.addEventListener('click', (e) => {
              this.$store.dispatch('setDrawState', false)
            })
          }
        }
      }
    },
    addPrimitives (positions) {
      const min = Cesium.Cartesian3.fromDegrees(positions[0][0], positions[0][1], 180)
      const max = Cesium.Cartesian3.fromDegrees(positions[2][0], positions[2][1], 185)
      const dimensions = Cesium.Cartesian3.subtract(max, min, new Cesium.Cartesian3())
      const center = Cesium.Cartesian3.midpoint(max, min, new Cesium.Cartesian3())

      const box = Cesium.BoxGeometry.fromDimensions({
        vertexFormat: Cesium.VertexFormat.POSITION_ONLY,
        dimensions: dimensions
      })

      const geometry = Cesium.BoxGeometry.createGeometry(box)

      let modelMatrix = Cesium.Transforms.eastNorthUpToFixedFrame(center)
      const hprRotation = Cesium.Matrix3.fromHeadingPitchRoll(
        new Cesium.HeadingPitchRoll(0, 0.0, 3.1415926 / 2)
      )
      const hpr = Cesium.Matrix4.fromRotationTranslation(hprRotation)
      modelMatrix = Cesium.Matrix4.multiply(modelMatrix, hpr, modelMatrix)

      this.$cesiumPlus.viewer.scene.primitives.add(
        new Cesium.ClassificationPrimitive({
          geometryInstances: new Cesium.GeometryInstance({
            geometry: geometry,
            modelMatrix: modelMatrix,
            attributes: {
              color: Cesium.ColorGeometryInstanceAttribute.fromColor(
                new Cesium.Color(1.0, 0.0, 0.0, 0.5)
              ),
              show: new Cesium.ShowGeometryInstanceAttribute(true)
            },
            id: 'volume'
          }),
          classificationType: Cesium.ClassificationType.CESIUM_3D_TILE
        })
      )

      // this.$cesiumPlus.viewer.scene.primitives.add(
      //   new Cesium.Primitive({
      //     geometryInstances: new Cesium.GeometryInstance({
      //       geometry: geometry,
      //       modelMatrix: modelMatrix,
      //       attributes: {
      //         color: Cesium.ColorGeometryInstanceAttribute.fromColor(
      //           new Cesium.Color(1.0, 0.0, 0.0, 0.5)
      //         ),
      //         show: new Cesium.ShowGeometryInstanceAttribute(true)
      //       },
      //       id: 'volume'
      //     }),
      //     appearance: new Cesium.PerInstanceColorAppearance()
      //   })
      // )
    }
    /// //////////////////////////////////////////////////// sppuc 设备功能 end ///////////////////////////////////////////////////////
  }
}
</script>

<!-- <style lang="scss">

</style> -->
<style lang="scss" scoped>
  .fullSize {
    width: 100%;
    height: 100%;
  }
</style>
